import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddUnitModal from './AddUnitModal';
import { hasPermission } from '../../../utils/permissions';
import { SUBORDINATE_LEVELS } from '../../../constants';
import {
  fetchInit,
  fetchUnits,
  fetchUnitById,
  addUnit,
  updateUnit,
  selectUnits,
  selectUnitLevels,
  selectVerifyModes,
  selectUnitsLoading,
  selectUnitsError,
  selectUnitsSuccessMessage,
  selectUnitsTotal,
  selectUnitsPage,
  selectUnitsPageSize,
  selectUnitsSortColumn,
  selectUnitsSortDirection,
  selectUnitsFilterName,
  selectSelectedUnit,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  deleteUnit,
  bulkDeleteUnits,
  clearError,
  clearSuccessMessage,
  clearSelectedUnit,
} from './slice';
import './Units.css';

function Units() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const units = useSelector(selectUnits);
  const unitLevels = useSelector(selectUnitLevels);
  const verifyModes = useSelector(selectVerifyModes);
  const loading = useSelector(selectUnitsLoading);
  const error = useSelector(selectUnitsError);
  const successMessage = useSelector(selectUnitsSuccessMessage);
  const total = useSelector(selectUnitsTotal);
  const page = useSelector(selectUnitsPage);
  const pageSize = useSelector(selectUnitsPageSize);
  const sortColumn = useSelector(selectUnitsSortColumn);
  const sortDirection = useSelector(selectUnitsSortDirection);
  const filterName = useSelector(selectUnitsFilterName);
  const selectedUnit = useSelector(selectSelectedUnit);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Check permissions - assuming 'unit' module with standard CRUD permissions
  const canAddUnit = hasPermission('unit', 'addUnit');
  const canEditUnit = hasPermission('unit', 'editUnit');
  const canDeleteUnit = hasPermission('unit', 'deleteUnit');

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUnits({ page, pageSize, sortColumn, sortDirection, filterName }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      // Refetch units to update the list
      dispatch(fetchUnits({ page, pageSize, sortColumn, sortDirection, filterName }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

  // Define table columns
  const columns = [
    {
      key: 'code',
      label: t('units.table.code'),
    },
    {
      key: 'name',
      label: t('units.table.name'),
    },
    {
      key: 'level',
      label: t('units.table.level'),
      render: (value) => {
        // value is the level ID (number)
        // Find the level object from unitLevels array
        const levelObj = unitLevels.find((level) => level.id === value);
        const levelName = levelObj ? levelObj.name : '-';

        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: levelObj ? '#f3e5f5' : '#e2e8f0',
              color: levelObj ? '#6a1b9a' : '#64748b',
            }}
          >
            {levelName}
          </Box>
        );
      },
    },
    {
      key: 'subordinateLevelId',
      label: t('units.table.parentUnit'),
      render: (value) => {
        // Find the subordinate level from the constants
        const subordinateLevel = SUBORDINATE_LEVELS.find((level) => level.id === value);
        const levelName = subordinateLevel ? t(subordinateLevel.nameKey) : '-';
        return levelName;
      },
    },
    {
      key: 'location',
      label: t('units.table.location'),
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    // Fetch unit details by ID
    dispatch(fetchUnitById(row.id));
    setShowEditDialog(true);
  };

  // Handle delete action - show confirmation dialog
  const handleDelete = (row) => {
    setUnitToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (unitToDelete?.id) {
      dispatch(deleteUnit(unitToDelete.id));
    }
    setShowDeleteDialog(false);
    setUnitToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setUnitToDelete(null);
  };

  // Handle bulk delete - show confirmation dialog
  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteUnits(selectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
    // fetchUnits will be called automatically by useEffect
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle add unit - show dialog
  const handleAddUnit = () => {
    dispatch(clearSelectedUnit());
    setShowAddDialog(true);
  };

  // Handle close add unit dialog
  const handleCloseAddUnit = () => {
    setShowAddDialog(false);
    dispatch(clearSelectedUnit());
  };

  // Confirm add unit
  const handleConfirmAddUnit = (unitData) => {
    dispatch(addUnit(unitData));
    setShowAddDialog(false);
  };

  // Handle close edit unit dialog
  const handleCloseEditUnit = () => {
    setShowEditDialog(false);
    dispatch(clearSelectedUnit());
  };

  // Confirm edit unit
  const handleConfirmEditUnit = (unitData) => {
    dispatch(updateUnit(unitData));
    setShowEditDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BusinessIcon fontSize="small" />
            <Typography variant="body2">{t('units.breadcrumb.catalog')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('units.breadcrumb.units')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('units.title')}
          </Typography>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {canAddUnit && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddUnit}
                id="addUnit"
                name="addUnit"
                data-testid="units-button-addUnit"
              >
                {t('units.addUnit')}
              </Button>
            )}
            {canDeleteUnit && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="units-button-deleteSelected"
              >
                {t('units.deleteSelected')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Input
                label={t('units.filterByName')}
                value={filterName}
                onChange={handleFilterChange}
                placeholder={t('units.filterByNamePlaceholder')}
                id="filterName"
                name="filterName"
                data-testid="units-input-filterName"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
          <Table
            id="units-table"
            columns={columns}
            data={units}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onEdit={canEditUnit ? handleEdit : null}
            onDelete={canDeleteUnit ? handleDelete : null}
            loading={loading}
            emptyMessage={t('units.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            sortableColumns={['code', 'name', 'level', 'subordinateLevelId', 'location']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('units.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('units.deleteDialog.message', {
              name: unitToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="units-button-cancelDelete"
          >
            {t('units.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="units-button-confirmDelete"
          >
            {t('units.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('units.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('units.bulkDeleteDialog.message', {
              count: selectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="units-button-cancelBulkDelete"
          >
            {t('units.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="units-button-confirmBulkDelete"
          >
            {t('units.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Unit Modal */}
      <AddUnitModal
        open={showAddDialog}
        onClose={handleCloseAddUnit}
        onConfirm={handleConfirmAddUnit}
        unitLevels={unitLevels}
        verifyModes={verifyModes}
      />

      {/* Edit Unit Modal */}
      <AddUnitModal
        open={showEditDialog}
        onClose={handleCloseEditUnit}
        onConfirm={handleConfirmEditUnit}
        unitLevels={unitLevels}
        verifyModes={verifyModes}
        unitData={selectedUnit}
        editMode={true}
      />
    </Box>
  );
}

export default Units;
